import React, { useState } from 'react';
import { Eye, EyeOff, Key } from 'lucide-react';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';

interface AccountTabErrors {
  nip?: string;
  password?: string;
  confirm_password?: string;
}

interface AccountTabProps {
  formData: any;
  handleChange: (field: string, value: string) => void;
  errors?: AccountTabErrors;
  isEdit?: boolean;
}

export default function AccountTab({
  formData,
  handleChange,
  errors = {},
  isEdit = false,
}: AccountTabProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Key className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900 mb-1">Informasi Akun</h3>
            <p className="text-sm text-blue-700">
              {isEdit
                ? 'Perbarui NIP dan password untuk akun login pegawai. Kosongkan password jika tidak ingin mengubahnya.'
                : 'Buat akun login untuk pegawai. NIP akan digunakan untuk login ke sistem.'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* NIP */}
        <div className="space-y-2">
          <Label htmlFor="nip">
            NIP <span className="text-red-500">*</span>
          </Label>
          <Input
            id="nip"
            type="text"
            value={formData.nip || ''}
            onChange={e => handleChange('nip', e.target.value)}
            placeholder="Masukkan NIP (contoh: EMP001)"
            className={`focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
              errors.nip ? 'border-red-500' : ''
            }`}
          />
          {errors.nip && <p className="text-sm text-red-600">{errors.nip}</p>}
          <p className="text-xs text-gray-500">NIP akan digunakan sebagai username untuk login</p>
        </div>

        {/* Password - Only show for new employee or if editing */}
        {!isEdit && (
          <div className="space-y-2">
            <Label htmlFor="password">
              Password <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password || ''}
                onChange={e => handleChange('password', e.target.value)}
                placeholder="Masukkan password"
                className={`w-full pr-10 focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                  errors.password ? 'border-red-500' : ''
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-red-600">{errors.password}</p>}
            <p className="text-xs text-gray-500">Minimal 6 karakter</p>
          </div>
        )}

        {/* Confirm Password - Only for new employee */}
        {!isEdit && (
          <div className="space-y-2">
            <Label htmlFor="confirm_password">
              Konfirmasi Password <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="confirm_password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirm_password || ''}
                onChange={e => handleChange('confirm_password', e.target.value)}
                placeholder="Konfirmasi password"
                className={`w-full pr-10 focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                  errors.confirm_password ? 'border-red-500' : ''
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.confirm_password && (
              <p className="text-sm text-red-600">{errors.confirm_password}</p>
            )}
          </div>
        )}

        {/* Password Update - Only for edit mode */}
        {isEdit && (
          <div className="space-y-2">
            <Label htmlFor="password">Password Baru (Opsional)</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password || ''}
                onChange={e => handleChange('password', e.target.value)}
                placeholder="Kosongkan jika tidak ingin mengubah"
                className="w-full pr-10 focus:outline-none focus:border-blue-500 transition-colors duration-200"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-red-600">{errors.password}</p>}
            <p className="text-xs text-gray-500">Kosongkan jika tidak ingin mengubah password</p>
          </div>
        )}
      </div>
    </div>
  );
}
